import React, { useEffect, Fragment, useState } from 'react';
import styled, { css } from 'styled-components';
import { styled as muiStyled } from '@mui/material/styles';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { DeleteForever, Replay } from '@mui/icons-material';
import { DateTime } from 'luxon';
import FlexboxContainer from 'components/UI/FlexboxContainer';
import RadioButton from 'components/UI/RadioButton';
import Input from 'components/UI/Input';
import ConsignmentEntry from 'data/ConsignmentEntry';
import { addAtIndex, removeAtIndex, update } from 'lib/Array';
import { SHIPPING } from 'constants/deliveryModes';
import { Color, FontSize, FontWeight, Percent, Size } from 'lib/styles';
import { IN_STOCK, MANUFACTURE } from '../../constants/consignmentEntry';
import ConsignmentCancelallationModal from 'components/Consignments/Modals/ConsignmentCancelationModal';
import { CANCEL_CONSIGNMENT_MODAL_TEXT } from 'constants/consignmentStates';
import { PRICE_TYPE } from 'constants/product';
import Link from 'view/Link';
import * as Cons from 'data/Consignment';
import { CONSIGNMENT_TYPES } from 'constants/order';
import OrderProcessPreview from './OrderProcessReview';
import { getItems } from 'lib/SessionStorage';
import { STORAGE_KEYS } from 'constants/general';
import { onlyWeekdaysAllowed } from 'utils/product';
import WarningBanner from 'components/UI/WarningBanner';
import { useReadPermission } from 'hooks/useUserRoles';
import { PermissionCode } from 'data/UserPermissions';
import { ReadOnlyWrapper } from 'components/common/ReadOnlyWrapper';
import { Main } from 'styles/themeColors';
import { ErrorRounded } from '@mui/icons-material'
import FlexboxContainerComponent from 'components/UI/FlexboxContainer';
import Tooltip from 'components/UI/Tooltip';
import { TEXT_LABELS } from 'constants/productDetails';
import { toast } from "components/UI/ToastNotification";

const Table = styled.table`
  width: ${Percent.full};
  border-spacing: 20px 0;
  margin-bottom: 30px;
`;

const TableRow = styled.tr<any>`
  font-size: 15px;

  &:first-child {
    font-weight: ${FontWeight.medium};
  }
`;

const TableHeader = styled.th<any>`
  padding-top: 12px;
  text-align: center;
  width: 30%;
  border-bottom: 1px solid ${Color.shuttleGray};
  margin-right: 10px;
  color: ${Color.black};
  font-size: ${FontSize.small};
  letter-spacing: 1px;

  &:first-child {
    text-align: left;
  }

  &:last-child {
    padding-right: 10px;
    text-align: right;
    margin-right: 0;
  }

  &:nth-child(2n) {
    width: 15%;
  }

  ${({ noBorder }) => noBorder && css`
    border: none;
    width: 3%;
  `}
`;

const TableData = styled.td<any>`
  text-align: center;
  vertical-align: middle;

  &:first-child:not(:only-child):not([rowspan]) {
    text-align: left;
    font-weight: ${FontWeight.bold};
  }

  &:first-child:not(:only-child)[rowspan] {
    text-align: left;
  }

  &:last-child:not(:only-child)[rowspan] {
    padding-right: 10px;
    text-align: right;
    margin-right: 0;
    font-weight: ${FontWeight.bold};
  }

  ${({ color }) => color && css`
    color: ${color};
  `}

  ${({ textDecoration }) => textDecoration && css`
    text-decoration: ${textDecoration};
  `}

  ${({ deleted }) => deleted && css`
    opacity: 0.5;
  `}
`;


const LabelText = styled.label`
  white-space: nowrap;
  margin: 0 2px;
`;

const StyledDeleteIcon = muiStyled(DeleteForever)<any>({
  cursor: 'pointer',
});

const StyledReplayIcon = muiStyled(Replay)<any>({
  cursor: 'pointer',
});

const DetailsContainer = styled.div`
  margin: 0 -20px;
`;

const PartNumber = styled.div<any>`
  font-size: ${FontSize.small};
  font-weight: ${FontWeight.medium};
  color: ${({ materialValidationSuccess }) => materialValidationSuccess === false ? Main.error2 : Color.darkerGray};

  ${({ deleted }) => deleted && css`
    opacity: 0.5;
  `}
`;

const getDateFromConsignmentEntry = (consignmentEntry: ConsignmentEntry, specificRadioValue?: string) => {
  const radioButtonValue = specificRadioValue || consignmentEntry.radioButtonValue
  return radioButtonValue === IN_STOCK
    ? consignmentEntry.committedDate
      ? consignmentEntry.committedDate
      : consignmentEntry.postOrderShippingCost && consignmentEntry.postOrderDeliveryDate
        ? consignmentEntry.postOrderDeliveryDate
        : consignmentEntry.preliminaryDeliveryDate
    : consignmentEntry.manufactureDate
      ? consignmentEntry.manufactureDate
      : consignmentEntry.postOrderShippingCost && consignmentEntry.postOrderDeliveryDate
        ? consignmentEntry.postOrderDeliveryDate
        : consignmentEntry.preliminaryDeliveryDate
}

interface Props {
  fulfillmentRequest: ConsignmentEntry[];
  deliveryMode: string;
  setFulfillmentRequest: (value: ConsignmentEntry[]) => void;
  renderOrderDetailsTitle: (heading: string) => any;
  consignmentType: string;
  consignmentDetailsResponse: Cons.ConsignmentDetails;
  setIsReviewPage: (value: boolean) => void;
  isReviewPage: boolean;
  setIsReviewPageMounted?: (value: boolean) => void;
  weekdaysOnly?: boolean;
  isOrderFulfillmentButtonDisabled?: boolean;
  onConfirm: () => void;
  disabled: boolean;
  isStockApiFailed: boolean
};

const OrderFulfillmentTable = ({
  fulfillmentRequest,
  setFulfillmentRequest,
  deliveryMode,
  renderOrderDetailsTitle,
  consignmentType = PRICE_TYPE.PURCHASE,
  consignmentDetailsResponse,
  setIsReviewPage,
  isReviewPage,
  setIsReviewPageMounted,
  weekdaysOnly = false,
  isOrderFulfillmentButtonDisabled = false,
  onConfirm,
  disabled,
  isStockApiFailed
}: Props) => {
  const originalFulfillmentRequest = consignmentDetailsResponse?.consignmentEntries || [];
  const [cancellationConsignmentPopup, setCancellationConsignmentPopup] = useState(false);
  const [indexValue, setIndexValue] = useState(0);
  const [isPreviewVisible, setIsPreviewVisible] = useState(false);
  const { readOnly: isSplitReadyOnly } = useReadPermission(PermissionCode.SPLIT_IN_STOCK_MANUFACTURE);
  const { readOnly: isRemoveReadOnly } = useReadPermission(PermissionCode.SPLIT_PRODUCT_REMOVAL);
  const { readOnly: isSplitRentalDatesReadOnly } = useReadPermission(PermissionCode.SPLIT_RENTAL_DATES);
  const [openDatePicker, setOpenDatePicker] = useState<{ index: number; type: 'purchase' | 'rentalStart' | 'rentalEnd' } | null>(null);

  useEffect(() => {

    if (consignmentDetailsResponse?.consignmentType === CONSIGNMENT_TYPES.RENT) {
      // Compare the original fullfillment request with fulfillment request if they are different or their length is not equal then show the preview button
      if (originalFulfillmentRequest.length !== fulfillmentRequest.length) {
        setIsPreviewVisible(true);
      }

      if (originalFulfillmentRequest.length === fulfillmentRequest.length) {
        const isDifferent = originalFulfillmentRequest.some((entry, index) => {
          const originalStart = new Date(entry.rentalStartDate).toDateString();
          const originalEnd = new Date(entry.rentalEndDate).toDateString();
          const currentStart = new Date(fulfillmentRequest[index].rentalStartDate).toDateString();
          const currentEnd = new Date(fulfillmentRequest[index].rentalEndDate).toDateString();

          return originalStart !== currentStart || originalEnd !== currentEnd;
        });

        if (!isDifferent && (fulfillmentRequest.every(entry => entry.radioButtonValue === IN_STOCK) || fulfillmentRequest.every(entry => entry.radioButtonValue === MANUFACTURE))) {
          setIsPreviewVisible(false);
        } else {
          setIsPreviewVisible(true);
        }
      }
    }
  }, [fulfillmentRequest]);


  useEffect(() => {
    let newFulfillmentRequest: ConsignmentEntry[] = [];

    // Deduplicate by ID to avoid processing the same product multiple times
    const uniqueEntries = fulfillmentRequest.filter((entry, index, self) =>
      index === self.findIndex(e => e.id === entry.id)
    );

    const isStockApiEnabled = consignmentDetailsResponse?.consignmentEntries?.[0]?.brandVendor?.isStockApiEnabled;

    uniqueEntries.forEach(consignmentEntry => {
      if (isStockApiEnabled) {
        const totalPlantStock = consignmentEntry.totalPlantStock ?? 0;

        if (totalPlantStock >= consignmentEntry.quantity) {
          // Full stock available - single IN_STOCK entry
          const stockEntry = {
            ...consignmentEntry,
            radioButtonValue: IN_STOCK,
            committedQuantity: consignmentEntry.quantity,
            manufactureQuantity: 0
          };
          stockEntry.committedDate = getDateFromConsignmentEntry(stockEntry);
          newFulfillmentRequest.push(stockEntry);
        }
        else if (totalPlantStock > 0) {
          // Partial stock - create exactly 2 entries: 1 stock + 1 manufacture
          const stockEntry = {
            ...consignmentEntry, // Preserves original quantity and all other fields
            radioButtonValue: IN_STOCK,
            committedQuantity: totalPlantStock,
            manufactureQuantity: 0
          };

          const manufactureEntry = {
            ...consignmentEntry, // Preserves original quantity and all other fields
            radioButtonValue: MANUFACTURE,
            committedQuantity: 0,
            manufactureQuantity: consignmentEntry.quantity - totalPlantStock
          };

          // Handle dates for both entries
          stockEntry.committedDate = getDateFromConsignmentEntry(stockEntry);
          manufactureEntry.manufactureDate = getDateFromConsignmentEntry(manufactureEntry);

          newFulfillmentRequest.push(stockEntry, manufactureEntry);
        }
        else {
          // No stock available - single MANUFACTURE entry
          const manufactureEntry = {
            ...consignmentEntry,
            radioButtonValue: MANUFACTURE,
            committedQuantity: 0,
            manufactureQuantity: consignmentEntry.quantity
          };
          manufactureEntry.manufactureDate = getDateFromConsignmentEntry(manufactureEntry);
          newFulfillmentRequest.push(manufactureEntry);
        }
      } else {
        newFulfillmentRequest.push({
          ...consignmentEntry,
          radioButtonValue: IN_STOCK,
          committedQuantity: 0,
          manufactureQuantity: 0
        });
      }
    });

    setFulfillmentRequest(newFulfillmentRequest);
  }, []);

  const getOriginalQuantity = (id: number): number | null => {
    return originalFulfillmentRequest.find((entry) => entry.id === id)?.quantity || null;
  }

  const onDateChange = (value: any, index: number, column: string = '') => {
    const newFulfillmentRequest = update(fulfillmentRequest, index, entry => {
      const key = column ? column : entry.radioButtonValue === IN_STOCK ? 'committedDate' : 'manufactureDate';

      return {
        ...entry,
        [key]: value.toString()
      };
    });

    setFulfillmentRequest(newFulfillmentRequest);
  };

  const onRadioButtonChange = (value: string, index: number) => {
    let newFulfillmentRequest = fulfillmentRequest;
    const foundDuplicateIndex = fulfillmentRequest.findIndex(
      (entry, i) =>
        entry.id === newFulfillmentRequest[index].id &&
        entry.radioButtonValue === value &&
        index !== i
    );

    if (foundDuplicateIndex !== -1) {
      newFulfillmentRequest = update(newFulfillmentRequest, index, entry => {
        return {
          ...entry,
          radioButtonValue: value,
          committedQuantity: value === IN_STOCK ? entry.committedQuantity + newFulfillmentRequest[foundDuplicateIndex].committedQuantity : 0,
          manufactureQuantity: value === IN_STOCK ? 0 : entry.committedQuantity + newFulfillmentRequest[foundDuplicateIndex].manufactureQuantity,
          committedDate: value === IN_STOCK ? getDateFromConsignmentEntry(entry, value) : '',
          manufactureDate: value === IN_STOCK ? '' : getDateFromConsignmentEntry(entry, value),
          cancelQuantity: 0
        };
      });

      newFulfillmentRequest = removeAtIndex(newFulfillmentRequest, foundDuplicateIndex);
    } else {
      newFulfillmentRequest = update(newFulfillmentRequest, index, entry => {
        return {
          ...entry,
          radioButtonValue: value,
          committedQuantity: value === IN_STOCK ? entry.committedQuantity : 0,
          manufactureQuantity: value === IN_STOCK ? 0 : entry.manufactureQuantity,
          committedDate: value === IN_STOCK ? getDateFromConsignmentEntry(entry, value) : '',
          manufactureDate: value === IN_STOCK ? '' : getDateFromConsignmentEntry(entry, value),
          cancelQuantity: 0
        };
      });
    }

    setFulfillmentRequest(newFulfillmentRequest);
  };

  const handleShowCancellationConsignmentPopup = () => setCancellationConsignmentPopup(true);
  const handleCloseCancellationConsignmentPopup = () => setCancellationConsignmentPopup(false);

  // onPagLoadSplitConsignment function removed - logic consolidated into single useEffect above

  const onInputChange = (key: string, secondaryKey: string, value: string, index: number) => {
    const currentEntry = fulfillmentRequest[index];
    const integerValue = parseInt(value.replace(/,/g, ''), 10);
    const validIntegerValue = value && !isNaN(integerValue)
      ? integerValue > currentEntry.quantity
        ? currentEntry.quantity
        : integerValue
      : 0;

    // Check if the input is for stock quantity (committedQuantity)
    if (key === 'committedQuantity' && consignmentDetailsResponse?.consignmentEntries?.[0]?.brandVendor?.isStockApiEnabled) {
      const totalPlantStock = currentEntry.totalPlantStock ?? 0;

      // Validate that the entered stock quantity doesn't exceed available stock

      if (value && !isNaN(integerValue) && integerValue > totalPlantStock && !isStockApiFailed) {
        toast.error('Cannot add more than available in stock');

        // Reset to the previous valid value (totalPlantStock or current committedQuantity)
        const resetValue = Math.min(currentEntry.committedQuantity || 0, totalPlantStock);
        const newFulfillmentRequest = update(fulfillmentRequest, index, entry => ({
          ...entry,
          committedQuantity: resetValue,
          [secondaryKey]: 0
        }));

        setFulfillmentRequest(newFulfillmentRequest);
        return;
      }
    }

    // Handle case when manufacture quantity equals or exceeds the ORIGINAL total quantity
    // This handles both: entering on stock line OR entering on manufacture line
    console.log('onInputChange:', { key, radioButtonValue: currentEntry.radioButtonValue, validIntegerValue, quantity: currentEntry.quantity });

    if (key === 'manufactureQuantity') {
      // Find the stock entry to get the original total quantity
      const stockEntry = fulfillmentRequest.find(
        (entry, i) =>
          entry.id === currentEntry.id &&
          entry.radioButtonValue === IN_STOCK &&
          !entry.isDeleted
      );
      const originalTotalQty = stockEntry ? stockEntry.quantity : currentEntry.quantity;
      console.log('originalTotalQty:', originalTotalQty, 'stockEntry found:', !!stockEntry);

      if (validIntegerValue >= originalTotalQty) {
        console.log('Manufacture >= original total! Merging...');
        let newFulfillmentRequest = fulfillmentRequest;

        // Find any other entry for the same product (stock or another manufacture)
        const otherEntryIndex = newFulfillmentRequest.findIndex(
          (entry, i) =>
            entry.id === currentEntry.id &&
            index !== i &&
            !entry.isDeleted
        );
        console.log('otherEntryIndex:', otherEntryIndex, 'total entries:', newFulfillmentRequest.length);

        if (otherEntryIndex !== -1) {
          // Update current manufacture entry to have the full original quantity
          newFulfillmentRequest = update(newFulfillmentRequest, index, entry => ({
            ...entry,
            radioButtonValue: MANUFACTURE,
            committedQuantity: 0,
            manufactureQuantity: originalTotalQty,
            quantity: originalTotalQty,
            committedDate: '',
            manufactureDate: getDateFromConsignmentEntry(entry, MANUFACTURE)
          }));

          // Remove the other entry (stock)
          newFulfillmentRequest = removeAtIndex(newFulfillmentRequest, otherEntryIndex);
          console.log('Removed other entry, new length:', newFulfillmentRequest.length);
        } else {
          // No other entry - just ensure current entry has correct values
          newFulfillmentRequest = update(newFulfillmentRequest, index, entry => ({
            ...entry,
            radioButtonValue: MANUFACTURE,
            committedQuantity: 0,
            manufactureQuantity: originalTotalQty,
            quantity: originalTotalQty,
            committedDate: '',
            manufactureDate: getDateFromConsignmentEntry(entry, MANUFACTURE)
          }));
        }

        console.log('Setting new fulfillment request');
        setFulfillmentRequest(newFulfillmentRequest);
        return;
      }
    }

    // Original logic for valid values
    const newFulfillmentRequest = update(fulfillmentRequest, index, entry => {
      return {
        ...entry,
        [key]: validIntegerValue,
        [secondaryKey]: 0
      };
    });

    setFulfillmentRequest(newFulfillmentRequest);
  };

  const onStockInputBlur = (index: number, key: string, enteredValue?: string) => {
    let newFulfillmentRequest = fulfillmentRequest;
    const currentEntry = newFulfillmentRequest[index];

    // Parse the entered value if provided
    const parsedEnteredValue = enteredValue ? parseInt(enteredValue.replace(/,/g, ''), 10) : null;
    const enteredQty = parsedEnteredValue !== null && !isNaN(parsedEnteredValue) ? parsedEnteredValue : null;

    // Handle case when manufacture quantity was entered on a stock line and equals or exceeds total
    // This is the fix for the async state issue - we use the entered value directly
    if (key === MANUFACTURE && currentEntry.radioButtonValue === IN_STOCK && enteredQty !== null && enteredQty >= currentEntry.quantity) {
      // Find any other entry for the same product
      const otherEntryIndex = newFulfillmentRequest.findIndex(
        (entry, i) =>
          entry.id === currentEntry.id &&
          index !== i &&
          !entry.isDeleted
      );

      const totalQty = currentEntry.quantity;

      if (otherEntryIndex !== -1) {
        // There's another entry - merge everything into current entry as manufacture
        newFulfillmentRequest = update(newFulfillmentRequest, index, entry => ({
          ...entry,
          radioButtonValue: MANUFACTURE,
          committedQuantity: 0,
          manufactureQuantity: totalQty,
          committedDate: '',
          manufactureDate: getDateFromConsignmentEntry(entry, MANUFACTURE)
        }));

        // Remove the other entry
        newFulfillmentRequest = removeAtIndex(newFulfillmentRequest, otherEntryIndex);
      } else {
        // No other entry - just convert current entry to manufacture
        newFulfillmentRequest = update(newFulfillmentRequest, index, entry => ({
          ...entry,
          radioButtonValue: MANUFACTURE,
          committedQuantity: 0,
          manufactureQuantity: totalQty,
          committedDate: '',
          manufactureDate: getDateFromConsignmentEntry(entry, MANUFACTURE)
        }));
      }

      setFulfillmentRequest(newFulfillmentRequest);
      return;
    }

    // Handle case when user enters 0 in stock field on a stock line
    // Remove the stock line and transfer quantity to manufacture
    if (key === IN_STOCK && currentEntry.radioButtonValue === IN_STOCK && currentEntry.committedQuantity === 0) {
      const totalQuantity = currentEntry.quantity;

      // Check if there's already a manufacture entry for this product
      const existingManufactureIndex = newFulfillmentRequest.findIndex(
        (entry, i) =>
          entry.id === currentEntry.id &&
          entry.radioButtonValue === MANUFACTURE &&
          index !== i &&
          !entry.isDeleted
      );

      if (existingManufactureIndex !== -1) {
        // Update manufacture entry to have the full original quantity from stock entry
        // Stock entry has the original quantity, manufacture entry has the remaining after split
        newFulfillmentRequest = update(newFulfillmentRequest, existingManufactureIndex, entry => ({
          ...entry,
          manufactureQuantity: totalQuantity,
          quantity: totalQuantity
        }));
        newFulfillmentRequest = removeAtIndex(newFulfillmentRequest, index);
      } else {
        // Convert current entry to manufacture - keep original quantity
        newFulfillmentRequest = update(newFulfillmentRequest, index, entry => ({
          ...entry,
          radioButtonValue: MANUFACTURE,
          committedQuantity: 0,
          manufactureQuantity: totalQuantity,
          committedDate: '',
          manufactureDate: getDateFromConsignmentEntry(entry, MANUFACTURE)
        }));
      }

      setFulfillmentRequest(newFulfillmentRequest);
      return;
    }

    // Handle case when user enters 0 in manufacture field on a manufacture line
    // Remove the manufacture line and revert stock to original quantity
    if (key === MANUFACTURE && currentEntry.radioButtonValue === MANUFACTURE && currentEntry.manufactureQuantity === 0) {
      // Check if there's already a stock entry for this product
      const existingStockIndex = newFulfillmentRequest.findIndex(
        (entry, i) =>
          entry.id === currentEntry.id &&
          entry.radioButtonValue === IN_STOCK &&
          index !== i &&
          !entry.isDeleted
      );

      if (existingStockIndex !== -1) {
        // Get the stock entry which has the original quantity
        const stockEntry = newFulfillmentRequest[existingStockIndex];
        const originalQuantity = stockEntry.quantity;
        // Update stock entry to have the full original quantity
        newFulfillmentRequest = update(newFulfillmentRequest, existingStockIndex, entry => ({
          ...entry,
          committedQuantity: originalQuantity
        }));
        newFulfillmentRequest = removeAtIndex(newFulfillmentRequest, index);
      } else {
        // Convert current entry to stock - use current quantity
        const totalQuantity = currentEntry.quantity;
        newFulfillmentRequest = update(newFulfillmentRequest, index, entry => ({
          ...entry,
          radioButtonValue: IN_STOCK,
          committedQuantity: totalQuantity,
          manufactureQuantity: 0,
          committedDate: getDateFromConsignmentEntry(entry, IN_STOCK),
          manufactureDate: ''
        }));
      }

      setFulfillmentRequest(newFulfillmentRequest);
      return;
    }

    // Handle case when manufacture quantity was entered on a stock line
    // and committedQuantity was set to 0 by onInputChange
    // This means user wants to move all quantity to manufacture
    if (key === MANUFACTURE && currentEntry.radioButtonValue === IN_STOCK && currentEntry.committedQuantity === 0) {
      // Find any other entry for the same product
      const otherEntryIndex = newFulfillmentRequest.findIndex(
        (entry, i) =>
          entry.id === currentEntry.id &&
          index !== i &&
          !entry.isDeleted
      );

      const totalQty = currentEntry.quantity;

      if (otherEntryIndex !== -1) {
        // There's another entry - merge everything into current entry as manufacture
        // Convert current entry to manufacture with full quantity
        newFulfillmentRequest = update(newFulfillmentRequest, index, entry => ({
          ...entry,
          radioButtonValue: MANUFACTURE,
          committedQuantity: 0,
          manufactureQuantity: totalQty,
          committedDate: '',
          manufactureDate: getDateFromConsignmentEntry(entry, MANUFACTURE)
        }));

        // Remove the other entry
        newFulfillmentRequest = removeAtIndex(newFulfillmentRequest, otherEntryIndex);
      } else {
        // No other entry - just convert current entry to manufacture
        newFulfillmentRequest = update(newFulfillmentRequest, index, entry => ({
          ...entry,
          radioButtonValue: MANUFACTURE,
          committedQuantity: 0,
          manufactureQuantity: totalQty,
          committedDate: '',
          manufactureDate: getDateFromConsignmentEntry(entry, MANUFACTURE)
        }));
      }

      setFulfillmentRequest(newFulfillmentRequest);
      return;
    }

    const foundManufactureIndex = newFulfillmentRequest.findIndex(
      (entry, i) =>
        entry.id === newFulfillmentRequest[index].id &&
        entry.radioButtonValue === MANUFACTURE &&
        index !== i &&
        !entry.isDeleted
    );

    const foundCancelIndex = newFulfillmentRequest.findIndex(
      entry =>
        entry.id === newFulfillmentRequest[index].id &&
        entry.isDeleted
    );

    if (foundManufactureIndex !== -1) {
      const manufactureQuantity = foundCancelIndex !== -1
        ? newFulfillmentRequest[index].quantity - newFulfillmentRequest[index].committedQuantity - newFulfillmentRequest[foundCancelIndex].manufactureQuantity
        : newFulfillmentRequest[index].quantity - newFulfillmentRequest[index].committedQuantity;

      newFulfillmentRequest = update(newFulfillmentRequest, foundManufactureIndex, entry => {
        return {
          ...entry,
          quantity: newFulfillmentRequest[index].quantity - newFulfillmentRequest[index].committedQuantity,
          manufactureQuantity
        };
      });

      if (newFulfillmentRequest[index].committedQuantity >= newFulfillmentRequest[index].quantity) {
        if (foundCancelIndex !== -1) {
          newFulfillmentRequest = removeAtIndex(newFulfillmentRequest, foundCancelIndex);
        }

        newFulfillmentRequest = removeAtIndex(newFulfillmentRequest, foundManufactureIndex);
      }

      if (
        newFulfillmentRequest[index + 1] &&
        newFulfillmentRequest[index].radioButtonValue === newFulfillmentRequest[index + 1].radioButtonValue &&
        newFulfillmentRequest[index].id === newFulfillmentRequest[index + 1].id
      ) {
        const quantity = key === IN_STOCK
          ? newFulfillmentRequest[index].quantity - newFulfillmentRequest[index].committedQuantity
          : newFulfillmentRequest[index].quantity - newFulfillmentRequest[index].manufactureQuantity;

        const manufactureQuantity = key === IN_STOCK
          ? newFulfillmentRequest[index].quantity - newFulfillmentRequest[index].committedQuantity
          : 0;

        const cancelQuantity = key === IN_STOCK
          ? 0
          : newFulfillmentRequest[index].quantity - newFulfillmentRequest[index].manufactureQuantity

        const newItem = {
          ...newFulfillmentRequest[index],
          quantity,
          committedQuantity: 0,
          manufactureQuantity: manufactureQuantity,
          cancelQuantity: cancelQuantity,
          isDeleted: key !== IN_STOCK,
          radioButtonValue: MANUFACTURE
        };

        newFulfillmentRequest = addAtIndex(newFulfillmentRequest, newItem, index + 1);
        newFulfillmentRequest = removeAtIndex(newFulfillmentRequest, index + 2);
      }

      if (foundCancelIndex !== -1) {
        if (newFulfillmentRequest[index]?.quantity <= newFulfillmentRequest[index]?.committedQuantity + newFulfillmentRequest[foundManufactureIndex]?.manufactureQuantity) {
          newFulfillmentRequest = removeAtIndex(newFulfillmentRequest, foundCancelIndex);
        } else {
          newFulfillmentRequest = update(newFulfillmentRequest, foundCancelIndex, entry => {
            return {
              ...entry,
              manufactureQuantity: newFulfillmentRequest[index]?.quantity - newFulfillmentRequest[index]?.committedQuantity - newFulfillmentRequest[foundManufactureIndex]?.manufactureQuantity
            };
          });
        }
      }
    } else if (foundCancelIndex !== -1) {
      const quantity = key === IN_STOCK
        ? newFulfillmentRequest[index].quantity - newFulfillmentRequest[index].committedQuantity
        : newFulfillmentRequest[index].quantity - newFulfillmentRequest[index].manufactureQuantity;

      if (quantity === 0) {
        newFulfillmentRequest = removeAtIndex(newFulfillmentRequest, foundCancelIndex);
        setFulfillmentRequest(newFulfillmentRequest);
        return;
      }
      const manufactureQuantity = key === IN_STOCK
        ? newFulfillmentRequest[index].quantity - newFulfillmentRequest[index].committedQuantity
        : 0;

      const cancelQuantity = key === IN_STOCK
        ? 0
        : newFulfillmentRequest[index].quantity - newFulfillmentRequest[index].manufactureQuantity

      newFulfillmentRequest = update(newFulfillmentRequest, foundCancelIndex, entry => {
        return {
          ...entry,
          quantity,
          committedQuantity: 0,
          manufactureQuantity: manufactureQuantity,
          cancelQuantity: cancelQuantity,
          isDeleted: key !== IN_STOCK,
          radioButtonValue: MANUFACTURE
        };
      });

      if (newFulfillmentRequest[index].manufactureQuantity >= newFulfillmentRequest[index].quantity) {
        newFulfillmentRequest = removeAtIndex(newFulfillmentRequest, foundCancelIndex);
      }
    } else {
      if (
        newFulfillmentRequest[index].committedQuantity >= newFulfillmentRequest[index].quantity ||
        newFulfillmentRequest[index].manufactureQuantity >= newFulfillmentRequest[index].quantity
      ) {
        return;
      }

      const quantity = key === IN_STOCK
        ? newFulfillmentRequest[index].quantity - newFulfillmentRequest[index].committedQuantity
        : newFulfillmentRequest[index].quantity - newFulfillmentRequest[index].manufactureQuantity;

      const manufactureQuantity = key === IN_STOCK
        ? newFulfillmentRequest[index].quantity - newFulfillmentRequest[index].committedQuantity
        : 0;

      const cancelQuantity = key === IN_STOCK
        ? 0
        : newFulfillmentRequest[index].quantity - newFulfillmentRequest[index].manufactureQuantity

      const newItem = {
        ...newFulfillmentRequest[index],
        quantity,
        committedQuantity: key === IN_STOCK ? 0 : newFulfillmentRequest[index].committedQuantity,
        manufactureQuantity,
        cancelQuantity,
        isDeleted: key !== IN_STOCK,
        radioButtonValue: MANUFACTURE
      };

      newFulfillmentRequest = addAtIndex(newFulfillmentRequest, newItem, index + 1);
    }

    setFulfillmentRequest(newFulfillmentRequest);
  };

  const onAddLineClick = (index: number) => {
    let newFulfillmentRequest = fulfillmentRequest;

    const canceledEntry = fulfillmentRequest[index];

    if (canceledEntry.radioButtonValue === IN_STOCK) {
      newFulfillmentRequest = update(fulfillmentRequest, index, (entry) => (
        {
          ...entry,
          committedQuantity: entry.cancelQuantity,
          cancelQuantity: 0,
          isDeleted: false,
        }
      ));
      setFulfillmentRequest(newFulfillmentRequest);
      return;
    }

    const foundManufactureIndex = newFulfillmentRequest.findIndex(
      (entry, i) =>
        entry.id === newFulfillmentRequest[index].id &&
        entry.radioButtonValue === MANUFACTURE &&
        index !== i &&
        !entry.isDeleted
    );

    if (foundManufactureIndex === -1) {
      newFulfillmentRequest = update(fulfillmentRequest, index, (entry) => (
        {
          ...entry,
          manufactureQuantity: entry.cancelQuantity,
          cancelQuantity: 0,
          isDeleted: false,
        }
      ));
      setFulfillmentRequest(newFulfillmentRequest);
      return;
    }

    const manufactureEntry = fulfillmentRequest[foundManufactureIndex];
    const originalQuantity = getOriginalQuantity(manufactureEntry.id);

    const sumOfCancelAndManufactureQuantities = canceledEntry.quantity + manufactureEntry.quantity;

    const newManufactureQuantity = (originalQuantity && sumOfCancelAndManufactureQuantities <= originalQuantity) || !originalQuantity ?
      sumOfCancelAndManufactureQuantities :
      originalQuantity;


    newFulfillmentRequest = update(fulfillmentRequest, index, (entry) => (
      {
        ...entry,
        manufactureQuantity: entry.cancelQuantity + manufactureEntry.manufactureQuantity,
        quantity: newManufactureQuantity,
        cancelQuantity: 0,
        isDeleted: false,
      }
    ));

    newFulfillmentRequest = removeAtIndex(newFulfillmentRequest, foundManufactureIndex);

    setFulfillmentRequest(newFulfillmentRequest);
    return;
  };

  const handleConsignmentCancellationConfirm = (consignmentCancelingReason: string) => {
    let newFulfillmentRequest = fulfillmentRequest;

    const foundCancelIndex = newFulfillmentRequest.findIndex(
      entry =>
        entry.id === newFulfillmentRequest[indexValue].id &&
        entry.isDeleted
    );

    const updateIndex = foundCancelIndex !== -1 ? foundCancelIndex : indexValue;

    newFulfillmentRequest = update(newFulfillmentRequest, updateIndex, entry => {
      const newCancelQuantity = foundCancelIndex !== -1
        ? newFulfillmentRequest[foundCancelIndex].cancelQuantity + newFulfillmentRequest[indexValue].committedQuantity + newFulfillmentRequest[indexValue].manufactureQuantity
        : newFulfillmentRequest[indexValue].committedQuantity > 0
          ? newFulfillmentRequest[indexValue].committedQuantity
          : newFulfillmentRequest[indexValue].manufactureQuantity > 0
            ? newFulfillmentRequest[indexValue].manufactureQuantity
            : newFulfillmentRequest[indexValue].quantity;

      const newQuantity = Math.max(newFulfillmentRequest[indexValue].quantity, newFulfillmentRequest[updateIndex].quantity);

      return {
        ...entry,
        quantity: newQuantity,
        committedQuantity: 0,
        manufactureQuantity: 0,
        cancelQuantity: newCancelQuantity,
        radioButtonValue: newFulfillmentRequest[updateIndex].radioButtonValue,
        isDeleted: true,
        cancellationReason: consignmentCancelingReason
      };
    });

    if (foundCancelIndex !== -1) {
      newFulfillmentRequest = removeAtIndex(newFulfillmentRequest, indexValue);
    }
    setFulfillmentRequest(newFulfillmentRequest);
  }

  const onDeleteLineClick = (index: number) => {
    setIndexValue(index);
    handleShowCancellationConsignmentPopup();
  };

  const cancellationConsignmentModal = (
    <ConsignmentCancelallationModal
      show={cancellationConsignmentPopup}
      onClose={handleCloseCancellationConsignmentPopup}
      onConfirm={handleConsignmentCancellationConfirm}
      title={CANCEL_CONSIGNMENT_MODAL_TEXT.TITLE}
      labels={[CANCEL_CONSIGNMENT_MODAL_TEXT.LABEL]}
      textareaPlaceholder={CANCEL_CONSIGNMENT_MODAL_TEXT.PLACEHOLDER}
    />
  );

  const renderOrderTableRows = (consignmentEntries: ConsignmentEntry[]) => {

    return consignmentEntries.map((consignmentEntry, index) => {
      const styledIcon = consignmentEntry.isDeleted
        ? <StyledReplayIcon color="primary" onClick={() => !isRemoveReadOnly && onAddLineClick(index)} />
        : <StyledDeleteIcon onClick={() => !isRemoveReadOnly && onDeleteLineClick(index)} />;

      const datePickerValue = getDateFromConsignmentEntry(consignmentEntry);

      let quantityToShow = (consignmentEntry.radioButtonValue === IN_STOCK ? consignmentEntry.committedQuantity : consignmentEntry.manufactureQuantity) || consignmentEntry?.quantity;

      return (
        <Fragment key={index}>
          <TableRow id={index}>
            <TableData deleted={consignmentEntry.isDeleted} textDecoration={consignmentEntry.isDeleted ? 'line-through' : 'none'}>
              <FlexboxContainer flexDirection="column" alignItems="flex-start">
                {consignmentEntry.productName}
                <FlexboxContainerComponent alignItems='center' gap="5px">
                  <PartNumber deleted={consignmentEntry.isDeleted} materialValidationSuccess={consignmentEntry.materialApiValidation}>SKU: {consignmentEntry.sku}</PartNumber>
                  {
                    consignmentEntry.materialApiValidation === false &&
                    <Tooltip
                      content={TEXT_LABELS.MATERIAL_API_VALIDATION_FAILED}
                      key="materialApiValidation"
                      size="large"
                      direction='right'
                    >
                      <ErrorRounded htmlColor={Main.error2} style={{ fontSize: 16, marginTop: 6 }} />
                    </Tooltip>
                  }
                </FlexboxContainerComponent>
              </FlexboxContainer>
            </TableData>
            <TableData>{quantityToShow}</TableData>
            <TableData>
              <FlexboxContainer alignItems="center">
                <RadioButton
                  id={`instock${index}`}
                  name={`consignment${index}`}
                  onChangeValue={(value) => onRadioButtonChange(value, index)}
                  value="instock"
                  checked={!!(consignmentEntry.radioButtonValue && consignmentEntry.radioButtonValue === IN_STOCK)}
                  disabled={consignmentEntry.isDeleted || consignmentEntry.id === 0 || (consignmentEntries[index - 1] && consignmentEntries[index - 1].id === consignmentEntry.id)}
                  readOnly={isSplitReadyOnly}
                />
                <LabelText htmlFor={`instock${index}`}>In stock</LabelText>
                <Input
                  id="in-stock-id"
                  value={consignmentEntry.isDeleted && consignmentEntry.radioButtonValue === IN_STOCK ? (consignmentEntry.cancelQuantity ?? 0).toString() : consignmentEntry.committedQuantity ? consignmentEntry.committedQuantity.toString() : '0'}
                  type="text"
                  width="70px"
                  textAlign='center'
                  disabled={consignmentEntry.isDeleted || (consignmentEntries[index - 1] && consignmentEntries[index - 1].id === consignmentEntry.id)}
                  onCurrencyChange={value => onInputChange('committedQuantity', 'manufactureQuantity', value, index)}
                  onBlur={(e) => onStockInputBlur(index, IN_STOCK)}
                  onClick={() => onRadioButtonChange(IN_STOCK, index)}
                  readOnly={isSplitReadyOnly}

                />
              </FlexboxContainer>
            </TableData>
            <TableData>
              <FlexboxContainer alignItems="center">
                <RadioButton
                  id={`manufacture${index}`}
                  name={`consignment${index}`}
                  onChangeValue={(value) => onRadioButtonChange(value, index)}
                  value="manufacture"
                  checked={!!(consignmentEntry.radioButtonValue && consignmentEntry.radioButtonValue === MANUFACTURE)}
                  disabled={consignmentEntry.isDeleted}
                  readOnly={isSplitReadyOnly}

                />
                <LabelText htmlFor={`manufacture${index}`}>Manufacture</LabelText>
                <Input
                  id="manufacture-id"
                  value={consignmentEntry.isDeleted && consignmentEntry.radioButtonValue === MANUFACTURE ? (consignmentEntry.cancelQuantity ?? 0).toString() : (consignmentEntry.manufactureQuantity ?? 0).toString()}
                  type="text"
                  width="60px"
                  textAlign='center'
                  disabled={consignmentEntry.isDeleted}
                  readOnly={isSplitReadyOnly}
                  onCurrencyChange={value => onInputChange('manufactureQuantity', 'committedQuantity', value, index)}
                  onBlur={(e) => onStockInputBlur(index, MANUFACTURE, e.target.value)}
                  onClick={() => onRadioButtonChange(MANUFACTURE, index)}
                />
              </FlexboxContainer>
            </TableData>
            {
              consignmentType === PRICE_TYPE.PURCHASE ?
                <TableData>
                  <FlexboxContainer margin='0 0 0 40px' flexDirection="column" width='148px' alignItems='center !important'>
                    <ReadOnlyWrapper readOnly={isSplitReadyOnly}>
                      <DatePicker
                        value={datePickerValue ? DateTime.fromISO(datePickerValue) : null}
                        onChange={value => onDateChange(value, index)}
                        format="yyyy MMM dd"
                        open={openDatePicker?.index === index && openDatePicker?.type === 'purchase'}
                        onOpen={() => setOpenDatePicker({ index, type: 'purchase' })}
                        onClose={() => setOpenDatePicker(null)}
                        slotProps={{
                          textField: {
                            size: 'small',
                            sx: { width: '63%' },
                            onClick: () => !isSplitReadyOnly && !consignmentEntry.isDeleted && setOpenDatePicker({ index, type: 'purchase' }),
                            InputProps: {
                              readOnly: true,
                              sx: { cursor: isSplitReadyOnly || consignmentEntry.isDeleted ? 'default' : 'pointer' }
                            }
                          }
                        }}
                        disabled={isSplitReadyOnly || consignmentEntry.isDeleted}
                      />
                    </ReadOnlyWrapper>
                  </FlexboxContainer>
                </TableData> :
                <>
                  <TableData>
                    <FlexboxContainer margin='0 0 0 40px' flexDirection="column" width='148px' alignItems='center !important'>
                      <ReadOnlyWrapper readOnly={isSplitRentalDatesReadOnly}>
                        <DatePicker
                          value={consignmentEntry.rentalStartDate ? DateTime.fromISO(consignmentEntry.rentalStartDate) : null}
                          onChange={value => onDateChange(value, index, 'rentalStartDate')}
                          format="yyyy MMM dd"
                          open={openDatePicker?.index === index && openDatePicker?.type === 'rentalStart'}
                          onOpen={() => setOpenDatePicker({ index, type: 'rentalStart' })}
                          onClose={() => setOpenDatePicker(null)}
                          slotProps={{
                            textField: {
                              size: 'small',
                              sx: { width: '63%' },
                              onClick: () => !isSplitRentalDatesReadOnly && !consignmentEntry.isDeleted && setOpenDatePicker({ index, type: 'rentalStart' }),
                              InputProps: {
                                readOnly: true,
                                sx: { cursor: isSplitRentalDatesReadOnly || consignmentEntry.isDeleted ? 'default' : 'pointer' }
                              }
                            }
                          }}
                          disabled={isSplitRentalDatesReadOnly || consignmentEntry.isDeleted}
                          shouldDisableDate={(date) => onlyWeekdaysAllowed(date, weekdaysOnly)}
                        />
                      </ReadOnlyWrapper>
                    </FlexboxContainer>
                  </TableData>
                  <TableData>
                    <FlexboxContainer margin='0 0 0 40px' flexDirection="column" width='148px' alignItems='center !important'>
                      <ReadOnlyWrapper readOnly={isSplitRentalDatesReadOnly}>
                        <DatePicker
                          value={consignmentEntry.rentalEndDate ? DateTime.fromISO(consignmentEntry.rentalEndDate) : null}
                          onChange={value => onDateChange(value, index, 'rentalEndDate')}
                          format="yyyy MMM dd"
                          open={openDatePicker?.index === index && openDatePicker?.type === 'rentalEnd'}
                          onOpen={() => setOpenDatePicker({ index, type: 'rentalEnd' })}
                          onClose={() => setOpenDatePicker(null)}
                          slotProps={{
                            textField: {
                              size: 'small',
                              sx: { width: '63%' },
                              onClick: () => !isSplitRentalDatesReadOnly && !consignmentEntry.isDeleted && setOpenDatePicker({ index, type: 'rentalEnd' }),
                              InputProps: {
                                readOnly: true,
                                sx: { cursor: isSplitRentalDatesReadOnly || consignmentEntry.isDeleted ? 'default' : 'pointer' }
                              }
                            }
                          }}
                          disabled={isSplitRentalDatesReadOnly || consignmentEntry.isDeleted}
                          shouldDisableDate={(date) => onlyWeekdaysAllowed(date, weekdaysOnly)}
                        />
                      </ReadOnlyWrapper>
                    </FlexboxContainer>
                  </TableData>
                </>
            }
            <TableData>
              <ReadOnlyWrapper readOnly={isRemoveReadOnly} >
                {styledIcon}
              </ReadOnlyWrapper>
            </TableData>
          </TableRow>
          <TableRow>
            <TableData>&nbsp;</TableData>
          </TableRow>
        </Fragment>
      );
    });
  }

  const onPreviewClick = () => {

    if (isOrderFulfillmentButtonDisabled) return;
    const countryId = consignmentDetailsResponse.countryId;
    const countries = getItems(STORAGE_KEYS.COUNTRIES)
    const country = countries?.find((x: any) => x.id === countryId);
    // Saving the data in sessionStorage to be used in OrderProcessPreview screen
    sessionStorage.setItem(STORAGE_KEYS.PREVIEW_ROUTE_DATA, JSON.stringify({ fulfillmentRequest, consignmentDetailsResponse, country: country?.countryIso, originalFulfillmentRequest }));
    setIsReviewPage(true);
  }

  if (isReviewPage) return (
    <OrderProcessPreview
      consignmentId={consignmentDetailsResponse.consignmentId}
      setIsReviewPageMounted={setIsReviewPageMounted}
      setIsReviewPage={setIsReviewPage}
      onConfirm={onConfirm}
      countryId={consignmentDetailsResponse.countryId}
      totalDiscounts={consignmentDetailsResponse.totalDiscounts}
    />
  )

  return (
    <>
      {
        fulfillmentRequest.length > 0 ? (
          <>
            {renderOrderDetailsTitle('Provide fulfillment details')}

            {isPreviewVisible && !disabled &&
              <WarningBanner
                message={<>
                  You have adjusted your order. You can click <Link className='text-underlined' onClick={onPreviewClick} >Preview</Link> to view the updated prices and how the order will be split.
                  Make sure the customer is aware of any changes. After reviewing the changes with the customer, click&nbsp;<Link onClick={onConfirm} className='text-underlined'>'Confirm'</Link> to finalize the order.
                  The customer will receive an email with the updated order details.
                </>}
              />
            }

            <DetailsContainer>
              <Table>
                <thead>
                  <TableRow>
                    <TableHeader>Products</TableHeader>
                    <TableHeader>Qty</TableHeader>
                    <TableHeader colSpan={2}>Availability</TableHeader>
                    {
                      consignmentType === PRICE_TYPE.PURCHASE ?
                        <TableHeader>{deliveryMode === SHIPPING ? 'Est. ship date' : 'Est. pickup'}</TableHeader> :
                        <>
                          <TableHeader>Rent start</TableHeader>
                          <TableHeader>Rent end</TableHeader>
                        </>
                    }
                    <TableHeader noBorder>&nbsp;</TableHeader>
                  </TableRow>
                </thead>
                <tbody>
                  {renderOrderTableRows(fulfillmentRequest)}
                  {cancellationConsignmentModal}
                </tbody>
              </Table>
            </DetailsContainer>
          </>
        ) : null
      }
    </>
  );
};

export default OrderFulfillmentTable;

